/* The photos on a case study, and the photos on a social post.
 *
 * Both are the same screen (PhotoSet) over different rows, which is the point:
 * the old flows were two implementations of one job and drifted into different
 * bugs. The case-study one uploaded every file TWICE — once into a legacy
 * gallery it silently created and named after the case study, once into
 * content_photos — leaving two R2 objects for one photograph. The social one
 * created that gallery the moment you CLICKED the drop zone, before choosing a
 * file, and left an empty shell behind for every post anyone abandoned.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { PhotoSet } from './PhotoSet';
import {
  addGalleryPhotoToArticle, addGalleryPhotoToProject, deleteContentPhoto,
  deleteProjectPhoto, getArticle, getProjectPhotos, organiseCaseStudyPhotos, reorderArticlePhotos,
  reorderProjectPhotos, setArticleHero, setPhotoFile, uploadContentPhoto, uploadProjectPhoto,
} from '@/data/client';

export function CaseStudyPhotos({ articleId, heading = 'Project photos' }: {
  articleId: string;
  heading?: string;
}) {
  const qc = useQueryClient();
  return (
    <>
      <InterviewPhotos articleId={articleId} />
      {/* NOT ['article', id] itself. ContentIQ's article view caches the full
          article under that key, and this caches a reshaped { photos, mainId } —
          so any photo change (making one the main image, say) refetched the
          shared key with this shape, the article view lost `article` and the
          screen went blank, and this panel got the full article back with no
          mainId and showed the first photo as main as if the choice had not
          saved. Nested under it instead, so ContentIQ's own invalidations of
          ['article', id] still reach this. The article is refreshed after a
          change here because its HTML ends with the photo grid. */}
      <PhotoSet
        heading={heading}
        dropNote="Photos or videos. They go on this case study"
        acceptVideo
        emptyNote="No photos on this case study yet."
        queryKey={['article', articleId, 'photos']}
        onChanged={() => {
          void qc.invalidateQueries({ queryKey: ['article', articleId], exact: true });
          void qc.invalidateQueries({ queryKey: ['article', articleId, 'interview'] });
        }}
        load={async () => {
          const a = await getArticle(articleId);
          return { photos: a.photos, mainId: a.heroPhotoId };
        }}
        upload={(img) => uploadContentPhoto(articleId, img)}
        addExisting={(assetId) => addGalleryPhotoToArticle(articleId, assetId)}
        remove={(photoId) => deleteContentPhoto(photoId)}
        reorder={(ids) => reorderArticlePhotos(articleId, ids)}
        setMain={(photoId) => setArticleHero(articleId, photoId)}
        editFile={(photoId, input) => setPhotoFile(photoId, input)}
      />
    </>
  );
}

/* A case study written from an interview used to leave its photos behind on
 * the interview, which publishing archives — so the case study looked like it
 * had none. This offers to bring them over (shared, not copied) and to have AI
 * describe and name them; once they are here, it offers to rewrite the alt
 * text. Its own query key, for the reason given on PhotoSet's above. */
function InterviewPhotos({ articleId }: { articleId: string }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['article', articleId, 'interview'], queryFn: () => getArticle(articleId) });
  const [done, setDone] = useState('');
  const go = useMutation({
    mutationFn: (rewriteAlt: boolean) => organiseCaseStudyPhotos(articleId, { rewriteAlt }),
    onMutate: () => setDone(''),
    onSuccess: (r) => {
      setDone([
        r.attached ? `${r.attached} attached` : '',
        r.described ? `captions and alt text written for ${r.described}` : '',
        r.filed ? `${r.filed} filed into the case study folder` : '',
      ].filter(Boolean).join(' · ') || 'Nothing needed changing.');
      // A prefix: the article, its photo panel and this bar.
      void qc.invalidateQueries({ queryKey: ['article', articleId] });
    },
  });

  const waiting = data?.interviewPhotos ?? 0;
  const have = data?.photos.length ?? 0;
  if (!data || (!waiting && !have)) return null;

  return (
    <div className="es-photobar">
      <Icon name={waiting ? 'image' : 'sparkles'} size={15} />
      <span className="es-photobar__text">
        {waiting
          ? `${waiting} photo${waiting === 1 ? '' : 's'} from the interview ${waiting === 1 ? 'is' : 'are'} not on this case study yet. Attaching writes captions, alt text and file names from the photos and the interview, files them into this case study’s folder, and adds the photo grid to the end of the HTML.`
          : 'Captions, alt text and file names can be worked out by AI from the photos and the interview.'}
        {done && <strong> {done}</strong>}
        {go.isError && <strong> {go.error instanceof Error ? go.error.message.replace(/^API \d+:\s*/, '') : 'That did not work.'}</strong>}
      </span>
      <Button variant={waiting ? 'primary' : 'outline'} size="sm" disabled={go.isPending}
        iconLeft={<Icon name="sparkles" size={14} />}
        onClick={() => go.mutate(!waiting)}>
        {go.isPending ? 'Working…' : waiting ? 'Attach & describe' : 'Rewrite captions, alt text & names'}
      </Button>
    </div>
  );
}

/* A social post's images. No main image: the post carries up to four and their
   order decides which, so a "lead" would be a second, contradictory answer. */
export function ProjectPhotos({ projectId, heading = 'Photos for this post' }: {
  projectId: string;
  heading?: string;
}) {
  return (
    <PhotoSet
      heading={heading}
      dropNote="The first four go out with the post"
      emptyNote="No photos on this post yet."
      queryKey={['projectPhotos', projectId]}
      load={async () => ({ photos: (await getProjectPhotos(projectId)).photos, mainId: '' })}
      upload={(img) => uploadProjectPhoto(projectId, img)}
      addExisting={(assetId) => addGalleryPhotoToProject(projectId, assetId)}
      remove={(rowId) => deleteProjectPhoto(projectId, rowId)}
      reorder={(ids) => reorderProjectPhotos(projectId, ids)}
    />
  );
}
